// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import 'leaflet/dist/leaflet.css';
import './styles.css';
import { loadElection, type ElectionData, type Seat } from './data.ts';
import { initTooltip, hideTooltip } from './tooltip.ts';
import { initGlossary } from './glossary.ts';
import { openSeatDrawer, closeSeatDrawer } from './drilldown.ts';
import { openAbout } from './about.ts';
import { GROUP_META } from './parties.ts';
import { esc, fullName } from './utils/format.ts';

import { renderOverview } from './views/overview.ts';
import { renderMap } from './views/map.ts';
import { renderPendulum } from './views/pendulum.ts';
import { renderPreferences } from './views/preferences.ts';
import { renderFirstPrefs } from './views/firstprefs.ts';
import { renderRankings } from './views/rankings.ts';
import { renderExplorer } from './views/explorer.ts';
import { renderDistribution } from './views/distribution.ts';
import { renderInsights } from './views/insights.ts';

export interface Ctx {
  data: ElectionData;
  seatByName: Map<string, Seat>;
  navigate: (view: string, opts?: { seat?: string; replace?: boolean }) => void;
  openSeat: (name: string) => void;
}

interface ViewDef {
  key: string;
  label: string;
  render: (root: HTMLElement, ctx: Ctx) => void | Promise<void>;
}

const VIEWS: ViewDef[] = [
  { key: 'overview', label: 'Overview', render: renderOverview },
  { key: 'map', label: 'Seat map', render: renderMap },
  { key: 'pendulum', label: 'Pendulum', render: renderPendulum },
  { key: 'preferences', label: 'Preferences', render: renderPreferences },
  { key: 'firstprefs', label: 'First preferences', render: renderFirstPrefs },
  { key: 'rankings', label: 'Rankings', render: renderRankings },
  { key: 'explorer', label: 'Explorer', render: renderExplorer },
  { key: 'distribution', label: 'Distribution', render: renderDistribution },
  { key: 'insights', label: 'Insights', render: renderInsights },
];

function parseHash(): { view: string; seat: string | null } {
  const h = location.hash.replace(/^#/, '');
  const p = new URLSearchParams(h);
  let view = p.get('v') || 'overview';
  if (!VIEWS.some((v) => v.key === view)) view = 'overview';
  return { view, seat: p.get('seat') };
}

async function boot(): Promise<void> {
  const app = document.getElementById('app')!;
  app.innerHTML = `
    <header class="site-header">
      <div class="header-top">
        <div class="header-brand">
          <svg class="logo" viewBox="0 0 32 32" aria-hidden="true"><rect width="32" height="32" rx="7" fill="#fff"/><rect x="7" y="13" width="18" height="12" rx="1.5" fill="#1e3a5f"/><rect x="14.4" y="5" width="3.2" height="7.5" rx="0.6" fill="#e0403f" transform="rotate(-12 16 8.75)"/><path d="M13 19.6l2 2 4-4" fill="none" stroke="#e0403f" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>
          <div class="header-titles">
            <h1>Federal Election Results</h1>
            <div class="sub">Australia · 2025 House of Representatives</div>
          </div>
        </div>
        <div class="header-actions">
          <div class="search-wrap">
            <div class="header-search">
              <span aria-hidden="true">🔍</span>
              <input id="seat-search" type="search" placeholder="Find your seat…" autocomplete="off" aria-label="Search for an electorate" />
            </div>
            <div class="search-results" id="search-results" role="listbox"></div>
          </div>
          <button class="icon-btn" id="about-btn" title="About this site" aria-label="About this site">?</button>
        </div>
      </div>
      <nav class="site-nav"><div class="nav-inner" id="nav-tabs"></div></nav>
    </header>
    <main class="main-content" id="main"></main>
    <footer class="site-footer">
      <div class="footer-inner">
        <div class="src">
          Source: <a href="https://results.aec.gov.au/31496/Website/HouseDefault-31496.htm" target="_blank" rel="noopener">Australian Electoral Commission — Tally Room</a>, 2025 federal election (final results). Boundaries: Australian Electoral Commission / Digital Atlas of Australia. Not affiliated with the AEC.
        </div>
        <div>Built by <a href="https://benrichardson.dev/">benrichardson.dev</a> · <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more tools &amp; sites</a></div>
      </div>
    </footer>`;

  let data: ElectionData;
  const main = document.getElementById('main')!;
  main.innerHTML = `<div class="panel"><div class="skeleton" style="height:120px"></div></div><div class="panel"><div class="skeleton" style="height:340px"></div></div>`;
  try {
    data = await loadElection();
  } catch (err) {
    main.innerHTML = `<div class="panel state-msg"><h2>Could not load results</h2><p>${esc((err as Error).message)}</p><button class="icon-btn" onclick="location.reload()" style="width:auto;padding:0 16px">Retry</button></div>`;
    return;
  }

  const seatByName = new Map(data.seats.map((s) => [s.name, s]));

  const ctx: Ctx = {
    data,
    seatByName,
    navigate(view, opts = {}) {
      const p = new URLSearchParams();
      p.set('v', view);
      if (opts.seat) p.set('seat', opts.seat);
      const hash = `#${p.toString()}`;
      if (opts.replace) history.replaceState(null, '', hash);
      else location.hash = hash;
      route();
    },
    openSeat(name) {
      const cur = parseHash();
      const p = new URLSearchParams();
      p.set('v', cur.view);
      p.set('seat', name);
      history.replaceState(null, '', `#${p.toString()}`);
      const seat = seatByName.get(name);
      if (seat) openSeatDrawer(seat, ctx, () => {
        const c = parseHash();
        const np = new URLSearchParams();
        np.set('v', c.view);
        history.replaceState(null, '', `#${np.toString()}`);
      });
    },
  };

  // Nav tabs
  const nav = document.getElementById('nav-tabs')!;
  nav.innerHTML = VIEWS.map((v) => `<button class="nav-tab" data-view="${v.key}">${v.label}</button>`).join('');
  nav.querySelectorAll<HTMLButtonElement>('.nav-tab').forEach((b) => {
    b.addEventListener('click', () => ctx.navigate(b.dataset.view!));
  });

  document.getElementById('about-btn')!.addEventListener('click', () => openAbout(data));

  setupSearch(ctx);

  let currentView = '';
  async function route(): Promise<void> {
    const { view, seat } = parseHash();
    nav.querySelectorAll<HTMLButtonElement>('.nav-tab').forEach((b) => {
      b.classList.toggle('active', b.dataset.view === view);
    });
    if (view !== currentView) {
      currentView = view;
      hideTooltip();
      const def = VIEWS.find((v) => v.key === view)!;
      main.innerHTML = '';
      const root = document.createElement('div');
      root.className = 'view';
      main.appendChild(root);
      window.scrollTo({ top: 0 });
      try {
        await def.render(root, ctx);
      } catch (err) {
        root.innerHTML = `<div class="panel state-msg"><h3>This view hit an error</h3><p>${esc((err as Error).message)}</p></div>`;
      }
    }
    if (seat && seatByName.has(seat)) {
      openSeatDrawer(seatByName.get(seat)!, ctx, () => {
        const c = parseHash();
        const np = new URLSearchParams();
        np.set('v', c.view);
        history.replaceState(null, '', `#${np.toString()}`);
      });
    } else {
      closeSeatDrawer();
    }
  }

  window.addEventListener('hashchange', route);
  if (!location.hash) history.replaceState(null, '', '#v=overview');
  route();
}

function setupSearch(ctx: Ctx): void {
  const input = document.getElementById('seat-search') as HTMLInputElement;
  const box = document.getElementById('search-results')!;
  const seats = ctx.data.seats;
  let active = -1;
  let matches: Seat[] = [];

  const close = () => { box.classList.remove('visible'); active = -1; };
  const run = () => {
    const q = input.value.trim().toLowerCase();
    if (!q) { close(); return; }
    matches = seats
      .filter((s) => s.name.toLowerCase().includes(q) || fullName(s.member.given, s.member.surname).toLowerCase().includes(q))
      .slice(0, 8);
    if (!matches.length) { box.innerHTML = `<div class="sr" style="cursor:default"><span class="meta">No electorate found</span></div>`; box.classList.add('visible'); return; }
    box.innerHTML = matches.map((s, i) => `
      <div class="sr${i === active ? ' active' : ''}" data-i="${i}" role="option">
        <span class="swatch" style="background:${GROUP_META[s.member.group].color}"></span>
        <span class="nm">${esc(s.name)}</span>
        <span class="meta">${esc(s.state)} · ${esc(fullName(s.member.given, s.member.surname))}</span>
      </div>`).join('');
    box.classList.add('visible');
    box.querySelectorAll<HTMLElement>('.sr[data-i]').forEach((el) => {
      el.addEventListener('click', () => { pick(matches[Number(el.dataset.i)]); });
    });
  };
  const pick = (s: Seat) => { input.value = ''; close(); ctx.openSeat(s.name); };

  let t: number | undefined;
  input.addEventListener('input', () => { window.clearTimeout(t); t = window.setTimeout(run, 200); });
  input.addEventListener('keydown', (e) => {
    if (!box.classList.contains('visible')) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); active = Math.min(matches.length - 1, active + 1); run(); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); active = Math.max(0, active - 1); run(); }
    else if (e.key === 'Enter' && active >= 0) { e.preventDefault(); pick(matches[active]); }
    else if (e.key === 'Escape') close();
  });
  document.addEventListener('click', (e) => { if (!(e.target as Element).closest('.search-wrap')) close(); });
}

initTooltip();
initGlossary();
boot();
